import type { CSSProperties } from 'react';

import { useTranslation } from 'react-i18next';

import { cn } from '@crm/shared/lib';
import {
    Select,
    SelectContent,
    SelectGroup,
    SelectLabel,
    SelectItem,
    SelectSeparator,
    SelectTrigger,
    SelectValue,
} from '@crm/shared/ui';

import { ColorDot } from './ColorDot';
import {
    COLOR_PICKER_BG_CLASS_NAME,
    COLOR_PICKER_BG_CSS_VAR,
    COLOR_PICKER_FOCUS_BG_CLASS_NAME,
    COLOR_PICKER_HIGHLIGHTED_BG_CLASS_NAME,
    COLOR_PICKER_HOVER_BG_CLASS_NAME,
} from './model/colorPickerStyles';

import type { TDealColumnColor } from '@crm/entities/deals-board';

type ColorPickerItemStyle = CSSProperties & Record<typeof COLOR_PICKER_BG_CSS_VAR, string>;

export const ColorPicker = ({
    columnColors,
    value: selectedColorValue,
    onChange,
    open: isOpen,
    submitOnEnter,
    onOpenChange,
    onSubmit,
    disabled = false,
}: {
    columnColors: TDealColumnColor[];
    value: string;
    onChange: (value: string) => void;
    open: boolean;
    submitOnEnter: boolean;
    onOpenChange: (open: boolean) => void;
    onSubmit: () => void;
    disabled?: boolean;
}) => {
    const { t } = useTranslation();
    const handleValueChange = (value: string | null) => {
        if (disabled || value === null) {
            return;
        }

        onChange(value);
    };

    return (
        <Select
            value={selectedColorValue}
            open={isOpen}
            disabled={disabled}
            onOpenChange={onOpenChange}
            onValueChange={handleValueChange}
        >
            <SelectTrigger
                className="border-none shadow-none focus-visible:border-transparent focus-visible:ring-0"
                disabled={disabled}
                type="button"
                onKeyDown={(event) => {
                    if (event.key !== 'Enter') {
                        return;
                    }

                    event.stopPropagation();

                    if (submitOnEnter) {
                        event.preventDefault();
                        onSubmit();
                    }
                }}
            >
                <SelectValue>
                    <ColorDot color={selectedColorValue} />
                </SelectValue>
            </SelectTrigger>
            <SelectContent alignItemWithTrigger={false} className="w-max min-w-0 p-1">
                <SelectGroup className="h-fit">
                    <SelectLabel className="text-foreground font-medium text-center">
                        {t('crm.deals.board.column.colorLabel')}
                    </SelectLabel>
                    <SelectSeparator />
                    <div className="grid grid-cols-3 gap-1">
                        {columnColors.map(
                            ({ color: colorValue, bgColor }) => (
                                <SelectItem
                                    key={colorValue}
                                    value={colorValue}
                                    showIndicator={false}
                                    className={cn(
                                        'h-8 w-8 justify-center p-0',
                                        COLOR_PICKER_HOVER_BG_CLASS_NAME,
                                        COLOR_PICKER_FOCUS_BG_CLASS_NAME,
                                        COLOR_PICKER_HIGHLIGHTED_BG_CLASS_NAME,
                                        selectedColorValue === colorValue &&
                                            COLOR_PICKER_BG_CLASS_NAME,
                                        'cursor-pointer'
                                    )}
                                    style={
                                        {
                                            [COLOR_PICKER_BG_CSS_VAR]: bgColor,
                                        } as ColorPickerItemStyle
                                    }
                                >
                                    <ColorDot color={colorValue} className="mx-auto size-3" />
                                </SelectItem>
                            )
                        )}
                    </div>
                </SelectGroup>
            </SelectContent>
        </Select>
    );
};
